import type { Element } from 'react'
import React, { Fragment } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { ErrorManagement } from 'app/common/error-management'
import { Layout } from 'app/common/layout'
import { media, ResponsiveContainer } from 'app/assets/styles'
import styled from 'styled-components'
import type { RootState } from 'shared/store/reducers'
import { H2, SmallText } from 'app/common/typography'
import { Breadcrumb } from 'app/common/breadcrumb'
import { FireBackground } from 'app/common/background'
import { colors } from 'shared/assets/styles'
import { useSelector } from 'react-redux'
import { SignupBlock } from './SignupBlock'
import { SPACING } from '../../../../app-v2/design-tokens'

const { greyBlue, veryDarkGrey, lightMayo } = colors

type Props = {
    breadcrumbKey: string,
    titleKey: string,
    baselineKey?: string,
    leftBlock: Element<any>,
    rightBlock?: Element<any>,
    nothingOnTheRight?: boolean,
    fromEcommerce?: boolean,
    noBackground?: boolean,
    className?: string,
    error: any,
    networkError: any,
    intl: IntlShape
}

const SplitScreenLayoutComponent = ({
    breadcrumbKey,
    titleKey,
    baselineKey,
    leftBlock,
    rightBlock,
    nothingOnTheRight = false,
    fromEcommerce,
    noBackground = false,
    className,
    error,
    networkError,
    intl
}: Props) => {
    const breadcrumb = [
        {
            title: intl.formatMessage({
                id: breadcrumbKey
            })
        }
    ]
    const { signinPageState } = useSelector((state: RootState) => state)
    return (
        <ErrorManagement error={error} networkError={networkError}>
            <Layout background="plain" border>
                <FireBackground
                    className="splitscreen"
                    backgroundColor={lightMayo}>
                    <Head>
                        {!fromEcommerce && (
                            <Breadcrumb breadcrumbArray={breadcrumb} />
                        )}
                        <Title>
                            <FormattedMessage id={titleKey}>
                                {(text: string) => <StyledH2>{text}</StyledH2>}
                            </FormattedMessage>
                            {baselineKey && (
                                <FormattedMessage id={baselineKey}>
                                    {(text: string) => (
                                        <SmallText>{text}</SmallText>
                                    )}
                                </FormattedMessage>
                            )}
                        </Title>
                    </Head>
                    <StyledContainer className={className}>
                        <Block className={className} noBackground={true}>
                            {leftBlock}
                        </Block>
                        {!nothingOnTheRight && (
                            <Block
                                noBackground={noBackground}
                                noPadding={!rightBlock}>
                                {rightBlock ? (
                                    <Fragment>{rightBlock}</Fragment>
                                ) : (
                                    <SignupBlock
                                        loginAnimation={
                                            signinPageState.loginAnimation
                                        }
                                    />
                                )}
                            </Block>
                        )}
                    </StyledContainer>
                </FireBackground>
            </Layout>
        </ErrorManagement>
    )
}

const Head = styled(ResponsiveContainer)`
    padding-top: 100px;
    ${media.medium`
        padding-top: 200px;
    `};
`

const Title = styled.div`
    text-align: center;
    padding-bottom: ${SPACING.XXXS};
    ${media.medium`
        text-align: left;
    `};
`

const StyledH2 = styled(H2)`
    margin-bottom: 10px;
`

const Block = styled.div`
    ${props =>
        props.noPadding
            ? 'background-color: transparent'
            : props.noBackground
            ? 'background-color: white'
            : `
                background: linear-gradient(90deg, ${veryDarkGrey}, ${greyBlue});
                background-repeat: no-repeat;
                background-size: auto;
                background-position: right 15%;
    `};
    border-radius: 10px;
    padding: ${props => (!props.noPadding ? '30px' : 0)};
    width: 100%;
    margin-top: 20px;
    ${media.medium`
    padding: ${props => (!props.noPadding ? SPACING.XXL : 0)};
        width: ${props =>
            !props.noPadding ? 'calc(43% - 90px)' : 'calc(57% - 0px)'};
        min-height: ${props =>
            props.className === 'signin' ? '500px' : 'auto'};
        margin-top: 0;
    `};

    ${media.large`
    padding: ${props => (!props.noPadding ? SPACING.XXL : 0)};
        width: ${props =>
            !props.noPadding ? 'calc(50% - 90px)' : 'calc(50% - 0px)'};
        min-height: ${props =>
            props.className === 'signin' ? '500px' : 'auto'};
        margin-top: 0;
    `};
`

const StyledContainer = styled(ResponsiveContainer)`
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: ${props =>
        props.className === 'signin' ? 'stretch' : 'flex-start'};
`

export const SplitScreenLayout = injectIntl(SplitScreenLayoutComponent)
